import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';

export interface GroupMemberItem {
  memberId: string;
  name: string;
}

export enum GroupType {
  Default = 'Default',
  Structmaster = 'Structmaster',
  License = 'License',
}

export interface GroupSummary {
  id: string;
  name: string;
  organizationId: string;
  organization: string;
  groupType?: GroupType;
}

export interface GroupItem {
  id: string;
  name: string;
  members: GroupMemberItem[];
  organizationId: string;
  organization: string;
  groupType?: GroupType;
}

export interface CreateGroupItem {
  name: string;
  organizationId: string;
  members: string[];
  groupType?: GroupType;
}

export interface UpdateGroupItem {
  name: string;
}

export class GroupSummaryStore extends DataStore<GroupSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/groups`, []);
  }

  public async loadWithTypes(query: Partial<Query>, groupTypes: GroupType[]) {
    return await this.handleCallAsync(async () => {
      const url = `${this.baseUrl}?${DataStore.buildUrl(query as Query)}&${groupTypes.map((g) => `groupTypes=${g}`).join('&')}`;
      var response = await this.httpService.get<QueryResult<GroupSummary>>(url);
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async getGroups(query: Partial<Query>, groupTypes: GroupType[]) {
    return await this.handleCallAsync(async () => {
      const url = `${this.baseUrl}?${DataStore.buildUrl(query as Query)}&${groupTypes.map((g) => `groupTypes=${g}`).join('&')}`;
      var response = await this.httpService.get<QueryResult<GroupSummary>>(url);
      return response.data.items;
    });
  }
}

export class GroupItemStore extends FormStore<GroupItem, CreateGroupItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/groups`);
  }

  groupMembersBasePath = 'users';

  public async addGroupMembers(id: string, userIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ members: string[] }, CommandResult<{ members: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.groupMembersBasePath}`,
        { members: userIds }
      );
      return result.data;
    });
  }

  public async deleteGroupMembers(id: string, userIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{ members: string[] }, CommandResult<{ members: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.groupMembersBasePath}`,
        { members: userIds }
      );
      return result.data;
    });
  }

  public async getUsersAlreadyOnGroupType(organizationId: string, groupType: GroupType, groupId?: string) {
    return await this.handleCallAsync(async () => {
      let response = await this.httpService.post<{ tenantId: string; groupType: GroupType; groupId?: string }, string[]>(
        `${this.baseUrl}/get-forbidden-users`,
        { tenantId: organizationId, groupType, groupId }
      );
      return response.data;
    });
  }
}
